import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  AreaChart,
  Area,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  ShoppingBag,
  Star,
  MessageSquare,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal,
  PlusCircle,
  Eye,
  Edit3,
  Pause,
  Play,
  Trash2,
  CheckCircle2,
  Clock,
  MapPin,
  ThumbsUp,
} from "lucide-react";

// Mock data
const earningsData = [
  { name: "Mon", earnings: 45000, trades: 8 },
  { name: "Tue", earnings: 52000, trades: 12 },
  { name: "Wed", earnings: 38000, trades: 6 },
  { name: "Thu", earnings: 61000, trades: 15 },
  { name: "Fri", earnings: 75000, trades: 18 },
  { name: "Sat", earnings: 82000, trades: 22 },
  { name: "Sun", earnings: 68000, trades: 16 },
];

const currencyDistribution = [
  { name: "USD", value: 45, color: "#22c55e" },
  { name: "EUR", value: 25, color: "#3b82f6" },
  { name: "GBP", value: 15, color: "#8b5cf6" },
  { name: "NGN", value: 10, color: "#f59e0b" },
  { name: "Others", value: 5, color: "#6b7280" },
];

const myListings = [
  {
    id: "LIST-001",
    title: "USD Cash - Best Rates in Lagos",
    type: "sell",
    currency: "USD",
    rate: 1550,
    minAmount: 100,
    maxAmount: 10000,
    status: "active",
    views: 245,
    inquiries: 12,
    trades: 8,
    rating: 4.8,
    location: "Lekki, Lagos",
    created: "2 days ago",
  },
  {
    id: "LIST-002",
    title: "Buying EUR at Premium Rate",
    type: "buy",
    currency: "EUR",
    rate: 1680,
    minAmount: 500,
    maxAmount: 5000,
    status: "active",
    views: 189,
    inquiries: 8,
    trades: 5,
    rating: 4.9,
    location: "Victoria Island, Lagos",
    created: "5 days ago",
  },
  {
    id: "LIST-003",
    title: "GBP Sterling - Instant Exchange",
    type: "sell",
    currency: "GBP",
    rate: 1950,
    minAmount: 200,
    maxAmount: 3000,
    status: "paused",
    views: 156,
    inquiries: 5,
    trades: 3,
    rating: 4.7,
    location: "Ikeja, Lagos",
    created: "1 week ago",
  },
];

const recentTrades = [
  {
    id: "TR-8291",
    customer: "John Doe",
    type: "buy",
    amount: 2500,
    currency: "USD",
    status: "completed",
    time: "10 min ago",
    rating: 5,
  },
  {
    id: "TR-8290",
    customer: "Sarah Smith",
    type: "sell",
    amount: 1500,
    currency: "EUR",
    status: "in_progress",
    time: "25 min ago",
    rating: null,
  },
  {
    id: "TR-8289",
    customer: "Mike Johnson",
    type: "buy",
    amount: 5000,
    currency: "USD",
    status: "completed",
    time: "1 hour ago",
    rating: 4,
  },
  {
    id: "TR-8288",
    customer: "Emma Wilson",
    type: "sell",
    amount: 800,
    currency: "GBP",
    status: "completed",
    time: "2 hours ago",
    rating: 5,
  },
];

const recentReviews = [
  {
    id: 1,
    customer: "David Brown",
    rating: 5,
    comment: "Excellent service, very fast transaction!",
    time: "2 hours ago",
    helpful: 12,
  },
  {
    id: 2,
    customer: "Lisa Anderson",
    rating: 4,
    comment: "Good rates, professional service",
    time: "5 hours ago",
    helpful: 8,
  },
  {
    id: 3,
    customer: "Tom Harris",
    rating: 5,
    comment: "Best forex vendor in Lagos, highly recommended",
    time: "1 day ago",
    helpful: 15,
  },
];

const statsCards = [
  {
    title: "Today's Earnings",
    value: "₦82,000",
    change: "+15.3%",
    trend: "up",
    icon: DollarSign,
    color: "from-emerald-500 to-teal-500",
    subtext: "From 22 trades",
    path: "/vendor/wallet",
  },
  {
    title: "Total Trades",
    value: "1,247",
    change: "+8.2%",
    trend: "up",
    icon: ShoppingBag,
    color: "from-blue-500 to-cyan-500",
    subtext: "This month",
    path: "/vendor/analytics",
  },
  {
    title: "Active Listings",
    value: "12",
    change: "+2",
    trend: "up",
    icon: TrendingUp,
    color: "from-violet-500 to-purple-500",
    subtext: "3 paused",
    path: "/vendor/listings",
  },
  {
    title: "Rating",
    value: "4.8",
    change: "+0.2",
    trend: "up",
    icon: Star,
    color: "from-amber-500 to-orange-500",
    subtext: "From 156 reviews",
    path: "/vendor/reviews",
  },
];

function StatCard({ stat }: { stat: (typeof statsCards)[0] }) {
  const Icon = stat.icon;
  const navigate = useNavigate();

  return (
    <Card
      className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 cursor-pointer"
      onClick={() => navigate(stat.path)}
    >
      <div
        className={cn(
          "absolute top-0 right-0 w-24 h-24 opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 bg-gradient-to-br",
          stat.color,
        )}
      />
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {stat.title}
        </CardTitle>
        <div className={cn("p-2 rounded-lg bg-gradient-to-br", stat.color)}>
          <Icon className="h-4 w-4 text-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">{stat.value}</span>
          <span
            className={cn(
              "text-xs font-medium flex items-center",
              stat.trend === "up" ? "text-emerald-500" : "text-red-500",
            )}
          >
            {stat.trend === "up" ? (
              <ArrowUpRight className="h-3 w-3 mr-0.5" />
            ) : (
              <ArrowDownRight className="h-3 w-3 mr-0.5" />
            )}
            {stat.change}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{stat.subtext}</p>
      </CardContent>
    </Card>
  );
}

export default function VendorDashboard() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState("7d");

  const handleListingAction = (action: string, listingId: number) => {
    // navigate(`/vendor/listing/${listingId}`);
    const id: string = `listing_${listingId + 1}`;
    navigate(`/vendor/listing/${id}`);

    toast({
      title: `${action} Listing`,
      description: `Action performed on ${listingId}`,
    });
  };

  const handleHelpfulReview = (reviewId: number) => {
    toast({
      title: "Marked as helpful",
      description: "Thank you for your feedback!",
    });
  };

  const handleReplyReview = (reviewId: number) => {
    toast({ title: "Reply", description: "Reply dialog would open here" });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            Vendor Dashboard
          </h1>
          <p className="text-muted-foreground">
            Manage your forex exchange business
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            className="bg-emerald-500 hover:bg-emerald-600"
            onClick={() => navigate("/vendor/listing/new")}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Create Listing
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => (
          <StatCard key={stat.title} stat={stat} />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Earnings Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Earnings Overview</CardTitle>
              <CardDescription>Your trading performance</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="text-xs cursor-pointer hover:bg-emerald-500/10"
              >
                <div className="h-2 w-2 rounded-full bg-emerald-500 mr-1" />
                Earnings
              </Badge>
              <Badge
                variant="outline"
                className="text-xs cursor-pointer hover:bg-blue-500/10"
              >
                <div className="h-2 w-2 rounded-full bg-blue-500 mr-1" />
                Trades
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={earningsData}>
                <defs>
                  <linearGradient
                    id="colorEarnings"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.2}
                />
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tickFormatter={(v) => `₦${v / 1000}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number, name: string) => [
                    name === "earnings" ? `₦${value.toLocaleString()}` : value,
                    name === "earnings" ? "Earnings" : "Trades",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="earnings"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorEarnings)"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="trades"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Currency Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Currency Distribution</CardTitle>
            <CardDescription>By trading volume</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={currencyDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {currencyDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number) => [`${value}%`, ""]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {currencyDistribution.map((curr) => (
                <div key={curr.name} className="flex items-center gap-1">
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: curr.color }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {curr.name}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Listings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">My Listings</CardTitle>
            <CardDescription>Manage your active listings</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/vendor/listings")}
          >
            View All
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Listing
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Type
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Rate
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Limits
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Stats
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Status
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {myListings.map((listing, index) => (
                  <tr key={listing.id} className="border-b hover:bg-muted/50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="text-sm font-medium">{listing.title}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {listing.location}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        className={cn(
                          "text-xs capitalize",
                          listing.type === "buy"
                            ? "bg-blue-500/20 text-blue-500"
                            : "bg-emerald-500/20 text-emerald-500",
                        )}
                      >
                        {listing.type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">
                      ₦{listing.rate.toLocaleString()}/{listing.currency}
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      ${listing.minAmount.toLocaleString()} - $
                      {listing.maxAmount.toLocaleString()}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" />
                          {listing.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {listing.inquiries}
                        </span>
                        <span className="flex items-center gap-1">
                          <ShoppingBag className="h-3 w-3" />
                          {listing.trades}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <Badge
                        className={cn(
                          "text-xs capitalize",
                          listing.status === "active"
                            ? "bg-emerald-500/20 text-emerald-500"
                            : "bg-amber-500/20 text-amber-500",
                        )}
                      >
                        {listing.status === "active" ? (
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                        ) : (
                          <Pause className="h-3 w-3 mr-1" />
                        )}
                        {listing.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleListingAction("View", index)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() =>
                            navigate(`/vendor/listing/edit/${listing.id}`)
                          }
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() =>
                                handleListingAction(
                                  listing.status === "active"
                                    ? "Pause"
                                    : "Activate",
                                  index,
                                )
                              }
                            >
                              {listing.status === "active" ? (
                                <Pause className="h-4 w-4 mr-2" />
                              ) : (
                                <Play className="h-4 w-4 mr-2" />
                              )}
                              {listing.status === "active"
                                ? "Pause"
                                : "Activate"}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-500"
                              onClick={() =>
                                handleListingAction("Delete", index)
                              }
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Bottom Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Trades */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Trades</CardTitle>
              <CardDescription>Your latest transactions</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/vendor/analytics")}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentTrades.map((trade) => (
                <div
                  key={trade.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-muted/50 cursor-pointer hover:bg-muted transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-emerald-500/20 text-emerald-500 text-sm">
                        {trade.customer.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{trade.customer}</p>
                      <p className="text-xs text-muted-foreground">
                        {trade.id} • {trade.time}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium">
                      {trade.type === "buy" ? "+" : "-"}
                      {trade.amount.toLocaleString()} {trade.currency}
                    </p>
                    <div className="flex items-center gap-2 justify-end">
                      <Badge
                        className={cn(
                          "text-xs",
                          trade.status === "completed"
                            ? "bg-emerald-500/20 text-emerald-500"
                            : "bg-amber-500/20 text-amber-500",
                        )}
                      >
                        {trade.status === "completed" ? (
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                        ) : (
                          <Clock className="h-3 w-3 mr-1" />
                        )}
                        {trade.status}
                      </Badge>
                      {trade.rating && (
                        <div className="flex items-center gap-0.5">
                          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs">{trade.rating}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Reviews */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Reviews</CardTitle>
              <CardDescription>What customers are saying</CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/vendor/reviews")}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentReviews.map((review) => (
                <div key={review.id} className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-violet-500/20 text-violet-500 text-xs">
                          {review.customer.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium">
                        {review.customer}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={cn(
                            "h-3 w-3",
                            i < review.rating
                              ? "fill-amber-400 text-amber-400"
                              : "text-muted-foreground",
                          )}
                        />
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {review.comment}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {review.time}
                  </p>
                  <div className="flex items-center gap-4 mt-2">
                    <button
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => handleHelpfulReview(review.id)}
                    >
                      <ThumbsUp className="h-3 w-3" />
                      Helpful ({review.helpful})
                    </button>
                    <button
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => handleReplyReview(review.id)}
                    >
                      <MessageSquare className="h-3 w-3" />
                      Reply
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
